import { FC, useEffect, useRef, useState } from "react";

import maplibregl from "maplibre-gl";

import BaseMapControl from "@/components/map/controls/BaseMapControl";
import MapControlCluster from "@/components/map/controls/MapControlCluster";
import MapFullScreenControl from "@/components/map/controls/MapFullScreenControl";
import MapRecenterControl from "@/components/map/controls/MapRecenterControl";
import MapZoomControl from "@/components/map/controls/MapZoomControl";
import { MAP_STYLES } from "@/constants/map";

/**
 * Only the CARTO styles — the Mapbox ones in `MAP_STYLES` need a token this map
 * deliberately does without.
 */
const TOKEN_FREE_BASE_MAPS = {
  street: MAP_STYLES.street,
  no_labels: MAP_STYLES.no_labels,
  lite: MAP_STYLES.lite,
  no_buildings: MAP_STYLES.no_buildings
};

type TokenFreeBaseMapKey = keyof typeof TOKEN_FREE_BASE_MAPS;

/** Style the map opens with; also the initial basemap-control selection. */
const INITIAL_BASE_MAP_KEY: TokenFreeBaseMapKey = "street";

type Props = {
  center: [number, number];
  zoom: number;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
};

/**
 * Standalone basemap for the Agentic AI conversation view.
 *
 * Deliberately does not go through `MapProvider` — that context carries the
 * whole dataset/analysis layer stack, none of which this panel needs. It owns
 * its maplibre instance, so it also owns the state its controls read (zoom
 * level, basemap); full screen is the page's call and arrives as props.
 */
const AgenticAIMap: FC<Props> = ({
  center,
  zoom,
  isFullScreen,
  onToggleFullScreen
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const initialViewRef = useRef<{ center: [number, number]; zoom: number }>({
    center,
    zoom
  });
  const [longitude, latitude] = center;
  const [zoomLevel, setZoomLevel] = useState(zoom);
  const [baseMapKey, setBaseMapKey] =
    useState<TokenFreeBaseMapKey>(INITIAL_BASE_MAP_KEY);

  // Imperative library owning a DOM node — a genuine external-system sync.
  // Constructed once from the initial props; later prop changes are synced to
  // the instance by the `jumpTo` effect below.
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return undefined;

    const initialView = initialViewRef.current;

    const map = new maplibregl.Map({
      container,
      style: TOKEN_FREE_BASE_MAPS[INITIAL_BASE_MAP_KEY].value,
      center: initialView.center,
      zoom: initialView.zoom,
      attributionControl: false
    });

    mapRef.current = map;
    map.on("zoom", () => setZoomLevel(map.getZoom()));

    return () => {
      mapRef.current = null;
      map.remove();
    };
  }, []);

  // Keep the imperative map camera aligned with the latest conversation view.
  useEffect(() => {
    mapRef.current?.jumpTo({ center: [longitude, latitude], zoom });
  }, [latitude, longitude, zoom]);

  const handleSelectBaseMap = (key: TokenFreeBaseMapKey) => {
    setBaseMapKey(key);
    mapRef.current?.setStyle(TOKEN_FREE_BASE_MAPS[key].value);
  };

  return (
    <section role="region" aria-label="Map" className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />

      <MapControlCluster>
        <MapRecenterControl
          className="mb-2"
          onRecenter={() =>
            mapRef.current?.flyTo({ center: [longitude, latitude], zoom })
          }
        />
        <MapFullScreenControl
          isFullScreen={isFullScreen}
          onToggle={onToggleFullScreen}
        />
        <MapZoomControl
          zoomLevel={zoomLevel}
          onZoomIn={() => mapRef.current?.zoomIn()}
          onZoomOut={() => mapRef.current?.zoomOut()}
        />
        <BaseMapControl
          styles={TOKEN_FREE_BASE_MAPS}
          selected={TOKEN_FREE_BASE_MAPS[baseMapKey]}
          onSelect={handleSelectBaseMap}
        />
      </MapControlCluster>
    </section>
  );
};

export default AgenticAIMap;
